import { Button, Form, Input, message } from 'antd';
import moment from 'moment';
import React, { CSSProperties, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { TRANSACTION_EVENT_TYPES } from '../../lib/constants';
import { storageService } from '../../lib/db/StorageService';
import {
  SupportedChains,
  SupportedFilterChains,
  TTxEventType,
} from '../../lib/models/NftModel';
import { Web3Service } from '../../lib/web3/Web3Service';
import { connectionAccountState } from '../../recoil/atoms';
import { FloatingInputLabel, SelectFloatingLabel } from '../FloatingInputLabel';
import FilterChainSelector from './FilterChainSelector';

const DEFAULT_STATE = {
  eventType: TRANSACTION_EVENT_TYPES['replay-transfer'].value as TTxEventType,
  transactionHash: '',
};

const AdminTransactions = () => {
  const [updating, setUpdating] = useState(false);
  const currentAccount = useRecoilValue(connectionAccountState);

  const [replayForm] = Form.useForm();

  const [formState, setFormState] = useState({
    chain: currentAccount.chain || 'bsc',
    ...DEFAULT_STATE,
  });

  const onChange = (values = {}) => {
    setFormState((state) => ({
      ...state,
      ...values,
    }));
  };

  const onUpdateTransaction = async () => {
    setUpdating(true);

    const { connection } = window;
    const web3Service = new Web3Service(connection);

    const payload: {
      timestamp: string;
      chain: SupportedChains;
      transactionHash: string;
      signature?: string;
    } = {
      timestamp: moment().format(),
      chain: formState.chain,
      transactionHash: formState.transactionHash,
    };

    const signResult = await web3Service.signPersonalMessage(
      JSON.stringify(payload),
      currentAccount.address,
    );

    if (!signResult || !signResult?.signature) {
      message.warn('Unable to get signature to update status');
      setUpdating(false);
      return;
    }

    payload.signature = signResult.signature;

    const response = await storageService.updateTransaction(
      formState.transactionHash,
      formState.eventType,
      payload,
    );

    if (!response || response?.error) {
      setUpdating(false);

      if (response?.statusCode === 400 && response?.message) {
        message.warn(response.message);
        return;
      }

      message.warn('Unable to replay the transaction');
      return;
    }

    setUpdating(false);

    message.success('The transaction was successfully replayed');

    setFormState({
      chain: currentAccount.chain || 'bsc',
      ...DEFAULT_STATE,
    });
  };

  return (
    <div className="admin-status-card">
      <Form form={replayForm} onFinish={onUpdateTransaction}>
        <div className="admin-status-card-title">
          Replay missing transaction
        </div>

        <FilterChainSelector
          value={formState.chain}
          onChange={(chain) =>
            onChange({ chain: chain as SupportedFilterChains })
          }
        />

        <SelectFloatingLabel
          label="Event Type"
          value={formState.eventType}
          options={Object.values(TRANSACTION_EVENT_TYPES)}
          onChange={(value) => onChange({ eventType: value })}
        />

        <FloatingInputLabel
          label="Transaction Hash"
          value={formState.transactionHash}
          style={
            {
              '--input-height': '97px',
              marginBottom: '5px',
            } as CSSProperties
          }
        >
          <Form.Item
            required
            className="form-item"
            name="transactionHash"
            validateFirst
            rules={[
              { required: true, message: 'Transaction Hash is required' },
            ]}
          >
            <Input.TextArea
              className="create-nft-input-description no-resize"
              rows={7}
              maxLength={300}
              allowClear
              name="transactionHash"
              onChange={({ target: { value, name } }) =>
                onChange({ [name]: value })
              }
            />
          </Form.Item>
        </FloatingInputLabel>

        <Button
          type="primary"
          className="nft-card-buy-button"
          htmlType="submit"
          style={{ width: '100%' }}
          disabled={updating}
          loading={updating}
        >
          <span className="normal-text-15">Update</span>
        </Button>
      </Form>
    </div>
  );
};

export default AdminTransactions;
